// src/ui/components/claweyHud.ts — Fixed-Position Mascot HUD in Lower-Left Corner with Discrete Shell Minimize (Story 1.3)
import { ClaweyMascot, ClaweyPose } from './claweyMascot';
import { SpeechBubble } from './speechBubble';
import { soundEngine } from '../../audio/soundEngine';

export const STORAGE_KEY_MINIMIZED = 'clawey_mascot_minimized';

export class ClaweyHud {
  private container: HTMLElement;
  private mascot: ClaweyMascot;
  private bubble: SpeechBubble;
  private mascotWrapper: HTMLElement;
  private shellMinimizeBtn: HTMLButtonElement;
  private minimizedShellPill: HTMLElement;
  private isMinimized: boolean = false;
  private hasUnreadBubble: boolean = false;

  private quotes: string[] = [
    "Ahoy young naturalist! What mysteries shall we unravel in the depths today?",
    "Keep your eyes keen for text clues! Even the tiniest barnacle tells a tale.",
    "A fine hypothesis! Observe how the tides turn when we test our reasoning.",
    "By the claws of Neptune, you are navigating these waters splendidly!",
  ];
  private quoteIndex = 0;

  constructor() {
    this.isMinimized = typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY_MINIMIZED) === 'true';

    this.container = document.createElement('aside');
    this.container.className = 'clawey-hud';
    this.container.style.position = 'fixed';
    this.container.style.bottom = '20px';
    this.container.style.left = '24px';
    this.container.style.right = 'auto';
    this.container.style.display = 'flex';
    this.container.style.flexDirection = 'column';
    this.container.style.alignItems = 'flex-start';
    this.container.style.gap = '10px';
    this.container.style.zIndex = '90';
    this.container.style.pointerEvents = 'none'; // allow click-through around it

    // 1. Socratic Speech Bubble
    this.bubble = new SpeechBubble({
      initialText: "Greetings, Tudor! Clawey at your service for today's expedition.",
    });
    this.bubble.getElement().style.pointerEvents = 'auto';

    // 2. Mascot Wrapper containing Clawey and the discrete shell minimize button
    this.mascotWrapper = document.createElement('div');
    this.mascotWrapper.className = 'clawey-mascot-wrapper';
    this.mascotWrapper.style.position = 'relative';
    this.mascotWrapper.style.display = 'inline-flex';
    this.mascotWrapper.style.alignItems = 'flex-end';
    this.mascotWrapper.style.gap = '8px';
    this.mascotWrapper.style.pointerEvents = 'auto';

    this.mascot = new ClaweyMascot({
      size: 130,
      initialPose: 'idle',
      onClick: () => this.handleMascotClick(),
    });

    // Discrete shell minimize button parked right on mascot's alcove
    this.shellMinimizeBtn = document.createElement('button');
    this.shellMinimizeBtn.className = 'clawey-shell-minimize-btn touch-target';
    this.shellMinimizeBtn.setAttribute('type', 'button');
    this.shellMinimizeBtn.setAttribute('title', 'Minimize Clawey (Focus Mode) 🐚');
    this.shellMinimizeBtn.setAttribute('aria-label', 'Minimize Clawey Mascot');
    this.shellMinimizeBtn.innerHTML = `
      <span style="font-size: 1.15rem; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));">🐚</span>
      <span style="font-size: 0.68rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: #1A365D;">Hide</span>
    `;
    this.shellMinimizeBtn.style.display = 'inline-flex';
    this.shellMinimizeBtn.style.alignItems = 'center';
    this.shellMinimizeBtn.style.gap = '4px';
    this.shellMinimizeBtn.style.padding = '4px 10px';
    this.shellMinimizeBtn.style.borderRadius = '16px';
    this.shellMinimizeBtn.style.border = '1.5px solid rgba(229, 169, 60, 0.45)';
    this.shellMinimizeBtn.style.background = 'radial-gradient(circle at center, #FFFDF9 60%, #F4E8D1 100%)';
    this.shellMinimizeBtn.style.boxShadow = '0 3px 8px rgba(10, 25, 47, 0.2)';
    this.shellMinimizeBtn.style.cursor = 'pointer';
    this.shellMinimizeBtn.style.position = 'absolute';
    this.shellMinimizeBtn.style.top = '-6px';
    this.shellMinimizeBtn.style.right = '-6px';
    this.shellMinimizeBtn.style.zIndex = '5';
    this.shellMinimizeBtn.style.transition = 'transform 0.2s ease, box-shadow 0.2s ease';

    this.shellMinimizeBtn.addEventListener('mouseenter', () => {
      this.shellMinimizeBtn.style.transform = 'scale(1.08)';
    });
    this.shellMinimizeBtn.addEventListener('mouseleave', () => {
      this.shellMinimizeBtn.style.transform = 'scale(1)';
    });
    this.shellMinimizeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMinimized();
    });

    this.mascotWrapper.appendChild(this.mascot.getElement());
    this.mascotWrapper.appendChild(this.shellMinimizeBtn);

    // 3. Minimized discrete shell pill (visible only when minimized)
    this.minimizedShellPill = document.createElement('button');
    this.minimizedShellPill.className = 'clawey-minimized-shell-pill touch-target';
    this.minimizedShellPill.setAttribute('type', 'button');
    this.minimizedShellPill.setAttribute('title', 'Restore Clawey 🐚');
    this.minimizedShellPill.setAttribute('aria-label', 'Restore Clawey Mascot');
    this.minimizedShellPill.innerHTML = `
      <div style="
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: radial-gradient(circle at 35% 35%, #1A365D 0%, #0A192F 100%);
        border: 2px solid #E5A93C;
        box-shadow: 0 4px 14px rgba(4, 10, 22, 0.5), 0 0 10px rgba(0, 229, 255, 0.35);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.35rem;
        transition: transform 0.2s ease;
      ">🐚</div>
      <div class="shell-unread-dot" style="
        position: absolute;
        top: -2px;
        right: -2px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #00E5FF;
        border: 2px solid #0A192F;
        display: none;
        box-shadow: 0 0 8px #00E5FF;
      "></div>
    `;
    this.minimizedShellPill.style.position = 'relative';
    this.minimizedShellPill.style.background = 'transparent';
    this.minimizedShellPill.style.border = 'none';
    this.minimizedShellPill.style.padding = '0';
    this.minimizedShellPill.style.cursor = 'pointer';
    this.minimizedShellPill.style.pointerEvents = 'auto';
    this.minimizedShellPill.style.display = 'none';

    this.minimizedShellPill.addEventListener('mouseenter', () => {
      this.minimizedShellPill.style.transform = 'scale(1.1)';
    });
    this.minimizedShellPill.addEventListener('mouseleave', () => {
      this.minimizedShellPill.style.transform = 'scale(1)';
    });
    this.minimizedShellPill.addEventListener('click', () => {
      this.toggleMinimized();
    });

    this.container.appendChild(this.bubble.getElement());
    this.container.appendChild(this.mascotWrapper);
    this.container.appendChild(this.minimizedShellPill);

    this.updateDisplayState();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  getBubble(): SpeechBubble {
    return this.bubble;
  }

  getMascot(): ClaweyMascot {
    return this.mascot;
  }

  getIsMinimized(): boolean {
    return this.isMinimized;
  }

  setMinimized(minimized: boolean): void {
    if (this.isMinimized === minimized) return;
    this.toggleMinimized(minimized);
  }

  toggleMinimized(targetState?: boolean): void {
    this.isMinimized = typeof targetState === 'boolean' ? targetState : !this.isMinimized;
    try {
      localStorage.setItem(STORAGE_KEY_MINIMIZED, String(this.isMinimized));
    } catch {
      // Ignored
    }
    soundEngine.playTap();
    if (!this.isMinimized) {
      this.hasUnreadBubble = false;
    }
    this.updateDisplayState();
  }

  private updateDisplayState(): void {
    if (this.isMinimized) {
      this.bubble.getElement().style.display = 'none';
      this.mascotWrapper.style.display = 'none';
      this.minimizedShellPill.style.display = 'block';

      const dot = this.minimizedShellPill.querySelector<HTMLElement>('.shell-unread-dot');
      if (dot) {
        dot.style.display = this.hasUnreadBubble ? 'block' : 'none';
      }
    } else {
      this.bubble.getElement().style.display = 'block';
      this.mascotWrapper.style.display = 'inline-flex';
      this.minimizedShellPill.style.display = 'none';
      const dot = this.minimizedShellPill.querySelector<HTMLElement>('.shell-unread-dot');
      if (dot) dot.style.display = 'none';
    }
  }

  say(message: string, pose: ClaweyPose = 'thinking', typewriter = true): void {
    this.mascot.setPose(pose);
    if (this.isMinimized) {
      this.hasUnreadBubble = true;
      const dot = this.minimizedShellPill.querySelector<HTMLElement>('.shell-unread-dot');
      if (dot) dot.style.display = 'block';
    }
    this.bubble.setText(message, typewriter, () => {
      // Revert to idle after celebration or thinking
      if (pose === 'celebrating') {
        setTimeout(() => this.mascot.setPose('idle'), 2500);
      }
    });
  }

  setPose(pose: ClaweyPose): void {
    this.mascot.setPose(pose);
  }

  getPose(): ClaweyPose {
    return this.mascot.getPose();
  }

  private handleMascotClick(): void {
    this.quoteIndex = (this.quoteIndex + 1) % this.quotes.length;
    const quote = this.quotes[this.quoteIndex] ?? this.quotes[0]!;
    this.say(quote, 'thinking', true);
  }
}
